import React, { memo, useMemo } from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import {
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { useUser } from "@clerk/nextjs"
import { Badge } from "@coval/ui"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatNumber } from "@/utils/formatting/format-number"

import {
  getCompositeResultData,
  type CompositeEvaluationResultJson,
} from "./composite-evaluation-types"

interface CompositeEvaluationReviewSummaryProps {
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  reportingMethod: string
}

/** Reads the annotation directly from props (fed by the tRPC query cache)
 *  so it stays in sync when the sub-rows' mutations invalidate the query. */
export const CompositeEvaluationReviewSummary: React.FC<CompositeEvaluationReviewSummaryProps> =
  memo(({ annotationsForMetric, metricOutput, reportingMethod }) => {
    const { user } = useUser()
    const userEmail = user?.emailAddresses?.[0]?.emailAddress

    const myAnnotation = useMemo(() => {
      return annotationsForMetric?.find(
        (annotation: Annotation) => annotation.assignee === userEmail,
      )
    }, [annotationsForMetric, userEmail])

    const resultJson = getCompositeResultData(metricOutput)
    const criteriaCount = resultJson?.individual_results?.length ?? 0
    const setValues = myAnnotation?.ground_truth_set_value

    // All criteria must be explicitly reviewed (tracked via ground_truth_set_value)
    const allReviewed =
      setValues?.length === criteriaCount &&
      criteriaCount > 0 &&
      setValues.every(
        (v) =>
          v === CRITERION_VALUE.MET ||
          v === CRITERION_VALUE.NOT_MET ||
          v === CRITERION_VALUE.UNKNOWN,
      )

    if (!allReviewed) {
      return <span className="text-coval-text-muted text-xs">-</span>
    }

    // Read computed score from ground_truth_json if available, otherwise compute from set_value
    const annotationJson = myAnnotation?.ground_truth_json as
      | CompositeEvaluationResultJson
      | undefined
    const summary = annotationJson?.summary

    // Derive counts from setValues when ground_truth_json summary is missing (legacy annotations)
    const metCount =
      summary?.criteria_met_count ??
      (setValues
        ? setValues.filter((v) => v === CRITERION_VALUE.MET).length
        : 0)
    const evaluated =
      summary?.criteria_evaluated_count ??
      (setValues
        ? setValues.filter(
            (v) => v === CRITERION_VALUE.MET || v === CRITERION_VALUE.NOT_MET,
          ).length
        : 0)
    const totalCount = summary?.criteria_total_count ?? criteriaCount

    if (reportingMethod === "all_criteria_met") {
      const passed = metCount === totalCount
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${passed ? yesNoBadgeColorClasses.success : yesNoBadgeColorClasses.danger}`}
        >
          {passed ? "Pass" : "Fail"}
        </span>
      )
    }

    if (reportingMethod === "percentage_of_criteria_met") {
      const pct = evaluated > 0 ? (metCount / evaluated) * 100 : 0
      return <Badge className="min-h-[24px] px-2">{formatNumber(pct)}%</Badge>
    }

    if (reportingMethod === "count_of_criteria_met") {
      return <Badge className="min-h-[24px] px-2">{metCount}</Badge>
    }

    // Default: show fraction
    return (
      <Badge className="min-h-[24px] px-2">
        {metCount}/{totalCount}
      </Badge>
    )
  })
CompositeEvaluationReviewSummary.displayName =
  "CompositeEvaluationReviewSummary"
